<template>
  <layout-base
    :enable-mobile-layout="enableMobileLayout"
    :notifications="notifications"
    @remove-notification="onRemoveNotification"
  >
    <template #sidebar>
      <side-bar
        v-if="showSidebar"
        :nav-items="navItems"
        :temporary="sidebarSize === 'temporary' ||layout.isMobile"
        :mini="sidebarSize === 'mini' || layout.isTablet"
        :logo="sidebarLogo"
        :logo-min="sidebarLogoSmall"
        :logo-url="customization?.logoUrl"
        @click:item="$emit('click-nav', $event)"
      >
        <template
          v-if="hasSidebarBottom"
          #aside-bottom="sidebar"
        >
          <sidebar-bottom
            v-if="!sidebarLoading"
            :sidebar="sidebar"
            @click-logout="emit('click-logout')"
          />
          <span v-else />
        </template>
      </side-bar>
    </template>
  </layout-base>
</template>

<script setup>
import * as dashboard from '@/dashboards'
import { layouts, piniaStoreModules } from 'frontendcommon-3'
import { useShellStore } from '@/store'
import { useRoute } from 'vue-router'
import { computed, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getActivePinia } from 'pinia'

const emit = defineEmits([
  'click-nav',
  'click-logout'
])

const pinia = getActivePinia()
const { LayoutBase } = layouts
const route = useRoute()
const shellStore = useShellStore()
const commonStore = piniaStoreModules.useCommon(pinia)

const layout = computed(() => commonStore.layout)
const { SidebarBottom, enableMobileLayout } = dashboard
const i18n = useI18n()

/*
* Customization
*/

shellStore.SET_SIDEBAR_LOADING(!!dashboard.sidebarLoading)
const sidebarLoading = computed(() => shellStore.sidebarLoading)
const sidebarSize = computed(() => shellStore.sidebarSize)
const showSidebar = computed(() => shellStore.showSidebar)
const customization = computed(() => shellStore.customization)

const sidebarLogo = computed(() => {
  if (sidebarLoading.value) return ''
  return customization.value ? customization.value.logo : dashboard.logo
})

const sidebarLogoSmall = computed(() => {
  if (sidebarLoading.value) return ''
  const logoOptions = customization.value && (customization.value.logoSmall || customization.value.logo)
  return customization.value ? logoOptions : dashboard.logoSmall
})

function injectCustomCss (id, css) {
  const oldStyle = document.getElementById(id)
  if (oldStyle) document.head.removeChild(oldStyle)
  if (!css) return
  const style = document.createElement('style')
  style.id = id
  style.appendChild(document.createTextNode(css))
  document.head.appendChild(style)
}

injectCustomCss('initial-style', dashboard.frontendcommonCss)
watch(customization, (customization) => {
  injectCustomCss('customization-style', customization?.colors)
}, { immediate: true })

/*
* Sidebar items
*/

const hasSidebarBottom = ref(!!SidebarBottom)
const currentRoutePath = computed(() => route.fullPath)
const sidebar = computed(() => shellStore.sidebar)

const computedSidebarItems = computed(() => {
  let items = sidebar.value.map((item) => {
    item = { ...item }
    // Translate text
    item.text = i18n.t(item.text)
    // Check if item is active
    item.isActive = (
      isActivePath(item.url, item.exact) ||
      (item.extraActivationPaths || []).some((path) => isActivePath(path, true))
    )
    return item
  })
  // Get item children
  items.forEach((item) => {
    item.childs = items.filter((child) => child.parent === item.id)
  })
  // Remove child items
  items = items.filter((item) => !item.parent)
  // Check if group items has some child active
  items.forEach(isGroupActive)
  // Check if group items are completly disabled
  items.forEach(isGroupDisabled)
  return items
})

function normalizePath (path, removeParams) {
  if (removeParams) path = path.split(/[?#]/)[0]
  path = path.replace(/\/$/, '')
  return path
}

function isActivePath (path, exact) {
  if (!path) return false
  return exact
    ? normalizePath(currentRoutePath.value) === normalizePath(path)
    : currentRoutePath.value.startsWith(normalizePath(path, true))
}

function isGroupActive (item) {
  item.isActive = (
    item.isActive ||
    item.childs.some((child) => child.isActive || isGroupActive(child))
  )
  return item.isActive
}

function isGroupDisabled (item) {
  const isDisabled = item.childs.length
    ? item.childs.every(isGroupDisabled)
    : item.isActivePath === false
  if (isDisabled) {
    item.tagText = item.tagText == null ? 'PRO' : item.tagText
    item.target = null
  }
  return isDisabled
}

const navItems = computed(() => {
  if (sidebarLoading.value) return []
  return computedSidebarItems.value
})

/*
* NOTIFICATION
*/

const notifications = computed(() => shellStore.notifications)
function onRemoveNotification (notification) {
  shellStore.REMOVE_NOTIFICATION(notification.id)
}
</script>
